import React, { useState, useEffect } from 'react';
import { FileSpreadsheet, Plus, Search, Filter, Download, Eye, Clock, CheckCircle, XCircle, AlertCircle, RefreshCw, Package } from 'lucide-react';
import { fetchAuthSession } from 'aws-amplify/auth';
import { DFPBOrderForm } from './DFPBOrderForm';
import { DFPBOrderEditModal } from './DFPBOrderEditModal';
import { useStudents, Student } from '../../contexts/StudentContext';
import { useToast } from '../../contexts/ToastContext';

interface DFPBOrder {
  orderId: string;
  studentEmail: string;
  studentName: string;
  counties: Array<{ state: string; county: string }>;
  orderType: 'new_list' | 'additional_records';
  groupTags: string[];
  additionalRecordsCount?: number;
  additionalRecordsReason?: string;
  submittedDate: string;
  deliveryDate?: string;
  status: 'new' | 'pending' | 'processing' | 'cancelled' | 'update' | 'delivered' | 'need_new_areas';
  downloadUrl?: string;
  notes?: string;
}

interface DFPBOrdersProps {
  initialOrderId?: string;
}

export function DFPBOrders({ initialOrderId }: DFPBOrdersProps) {
  const { dfpbStudents: students, loading: studentsLoading } = useStudents();
  const { showSuccess, showError } = useToast();
  const [orders, setOrders] = useState<DFPBOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [studentSearchTerm, setStudentSearchTerm] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [statusCounts, setStatusCounts] = useState<Record<string, number>>({});
  const [showStudentDropdown, setShowStudentDropdown] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState<Student | null>(null);
  const [editingOrder, setEditingOrder] = useState<DFGOrder | null>(null);
  const [handledInitialOrderId, setHandledInitialOrderId] = useState<string | null>(null);

  useEffect(() => {
    fetchOrders();
  }, []);

  // Reset handledInitialOrderId when initialOrderId changes to a different orderId
  useEffect(() => {
    if (initialOrderId && initialOrderId !== handledInitialOrderId) {
      setHandledInitialOrderId(null);
    }
  }, [initialOrderId]);

  // Handle initial order selection from deep link
  useEffect(() => {
    if (initialOrderId && orders.length > 0 && !editingOrder && initialOrderId !== handledInitialOrderId) {
      const order = orders.find(o => o.orderId === initialOrderId);
      if (order) {
        setEditingOrder(order);
        setHandledInitialOrderId(initialOrderId);
      } else {
        console.warn(`Order ${initialOrderId} not found`);
      }
    }
  }, [initialOrderId, orders, editingOrder, handledInitialOrderId]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.student-dropdown-container')) {
        setShowStudentDropdown(false);
      }
    };

    if (showStudentDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showStudentDropdown]);

  // Transform snake_case backend fields to camelCase frontend fields
  const transformOrder = (backendOrder: any): DFPBOrder => {
    return {
      orderId: backendOrder.order_id,
      studentEmail: backendOrder.student_email,
      studentName: backendOrder.student_name,
      counties: backendOrder.counties || [],
      orderType: backendOrder.order_type,
      groupTags: backendOrder.group_tags || [],
      additionalRecordsCount: backendOrder.quantity,
      additionalRecordsReason: backendOrder.admin_notes,
      submittedDate: backendOrder.submitted_date,
      deliveryDate: backendOrder.delivered_date,
      status: backendOrder.status,
      downloadUrl: backendOrder.download_link,
      notes: backendOrder.admin_notes
    };
  };

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken?.toString();

      if (!idToken) {
        throw new Error('No authentication token');
      }

      const response = await fetch(`${import.meta.env.VITE_API_URL}/admin/dfpb-orders`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch DFG orders');
      }

      const data = await response.json();
      const backendOrders = data.orders || [];

      // Transform snake_case to camelCase
      const ordersList = backendOrders.map(transformOrder);
      setOrders(ordersList);

      // Calculate status counts - FIX: Use actual backend statuses
      const counts: Record<string, number> = {
        new: 0,
        pending: 0,
        processing: 0,
        cancelled: 0,
        update: 0,
        delivered: 0,
        need_new_areas: 0
      };
      ordersList.forEach((order: DFPBOrder) => {
        if (counts[order.status] !== undefined) {
          counts[order.status]++;
        }
      });
      setStatusCounts(counts);
    } catch (error) {
      console.error('Error fetching DFG orders:', error);
      setOrders([]);
      setStatusCounts({});
    } finally {
      setLoading(false);
    }
  };

  // Filter students based on search term
  const filteredStudents = students.filter(student => {
    if (!studentSearchTerm) return true;
    const searchLower = studentSearchTerm.toLowerCase();
    return (
      student.name.toLowerCase().includes(searchLower) ||
      student.email.toLowerCase().includes(searchLower) ||
      student.tags?.some(tag => tag.toLowerCase().includes(searchLower))
    );
  });

  // Filter orders based on selected student and status
  const filteredOrders = orders.filter(order => {
    // Filter by status
    const matchesStatus = statusFilter === 'all' || order.status === statusFilter;

    // Filter by selected student
    const matchesStudent = !selectedStudent ||
      order.studentEmail === selectedStudent.email;

    return matchesStatus && matchesStudent;
  });

  // Handle status card click
  const handleStatusCardClick = (status: string) => {
    if (statusFilter === status) {
      setStatusFilter('all');
    } else {
      setStatusFilter(status);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      new: { color: 'bg-purple-100 text-purple-800', icon: Plus },
      pending: { color: 'bg-yellow-100 text-yellow-800', icon: Clock },
      processing: { color: 'bg-blue-100 text-blue-800', icon: RefreshCw },
      cancelled: { color: 'bg-red-100 text-red-800', icon: XCircle },
      update: { color: 'bg-orange-100 text-orange-800', icon: RefreshCw },
      delivered: { color: 'bg-green-100 text-green-800', icon: CheckCircle },
      need_new_areas: { color: 'bg-pink-100 text-pink-800', icon: AlertCircle }
    };

    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.new;
    const Icon = config.icon;

    // Format status label (e.g., "need_new_areas" -> "Need New Areas")
    const formatStatusLabel = (status: string) => {
      return status.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
    };

    return (
      <span className={`inline-flex items-center space-x-1 px-3 py-1 rounded-full text-xs font-semibold ${config.color}`}>
        <Icon className="w-3 h-3" />
        <span>{formatStatusLabel(status)}</span>
      </span>
    );
  };

  if (loading || studentsLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-tdb-accent mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading DFG orders...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center space-x-2">
            <FileSpreadsheet className="w-6 h-6 text-tdb-accent" />
            <span>DFPB Orders</span>
          </h2>
          <p className="text-gray-600 mt-1">Manage tax list orders for Debt Free Property Blueprint students</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center space-x-2 px-4 py-2 bg-tdb-accent text-white rounded-lg hover:bg-[#0ea5ef] transition-all font-semibold"
        >
          <Plus className="w-4 h-4" />
          <span>Create Order</span>
        </button>
      </div>

      {/* Status Cards */}
      <div className="bg-white rounded-2xl shadow-lg p-6">
        <h3 className="text-lg font-bold text-gray-900 mb-4">Order Status Overview</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
          {[
            { key: 'new', label: 'New', color: 'bg-purple-500' },
            { key: 'pending', label: 'Pending', color: 'bg-yellow-500' },
            { key: 'processing', label: 'Processing', color: 'bg-blue-500' },
            { key: 'cancelled', label: 'Cancelled', color: 'bg-red-500' },
            { key: 'update', label: 'Update', color: 'bg-orange-500' },
            { key: 'delivered', label: 'Delivered', color: 'bg-green-500' },
            { key: 'need_new_areas', label: 'Need New Areas', color: 'bg-pink-500' }
          ].map(status => {
            const count = statusCounts[status.key] || 0;
            const isActive = statusFilter === status.key;

            return (
              <button
                key={status.key}
                onClick={() => handleStatusCardClick(status.key)}
                className={`
                  relative p-4 rounded-xl transition-all duration-200
                  ${isActive
                    ? 'ring-4 ring-tdb-accent ring-opacity-50 shadow-xl scale-105'
                    : 'hover:shadow-lg hover:scale-105'
                  }
                  ${status.color} text-white
                `}
              >
                <div className="text-3xl font-bold mb-1">{count}</div>
                <div className="text-sm font-medium opacity-90">{status.label}</div>
                {isActive && (
                  <div className="absolute top-2 right-2">
                    <CheckCircle className="w-5 h-5" />
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Student Filter with Dropdown */}
      <div className="bg-white rounded-2xl shadow-lg p-6">
        <div className="flex flex-col md:flex-row md:items-center space-y-3 md:space-y-0 md:space-x-4">
          {/* Student Search with Dropdown */}
          <div className="relative flex-1 student-dropdown-container">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 z-10" />
            <input
              type="text"
              name="student-search-field"
              id="dfg-student-search"
              placeholder="Filter by student name, email, or tag (e.g., 'Sarah' or 'DFG_feb_2025')..."
              value={selectedStudent ? selectedStudent.name : studentSearchTerm}
              onChange={(e) => {
                setStudentSearchTerm(e.target.value);
                setSelectedStudent(null);
                setShowStudentDropdown(true);
              }}
              onFocus={() => setShowStudentDropdown(true)}
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck="false"
              data-lpignore="true"
              data-1p-ignore="true"
              data-bwignore="true"
              data-form-type="other"
              className="w-full pl-12 pr-10 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-tdb-accent focus:border-tdb-accent transition-all"
            />
            {selectedStudent && (
              <button
                onClick={() => {
                  setSelectedStudent(null);
                  setStudentSearchTerm('');
                }}
                className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <XCircle className="w-5 h-5" />
              </button>
            )}

            {/* Dropdown List */}
            {showStudentDropdown && filteredStudents.length > 0 && !selectedStudent && (
              <div className="absolute z-50 w-full mt-2 bg-white border-2 border-gray-200 rounded-xl shadow-xl max-h-96 overflow-y-auto">
                {filteredStudents.map((student) => (
                  <button
                    key={student.email}
                    onClick={() => {
                      setSelectedStudent(student);
                      setStudentSearchTerm('');
                      setShowStudentDropdown(false);
                    }}
                    className="w-full px-4 py-3 text-left hover:bg-tdb-accent hover:bg-opacity-10 transition-colors border-b border-gray-100 last:border-0"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="font-semibold text-gray-900">{student.name}</div>
                        <div className="text-sm text-gray-500">{student.email}</div>
                      </div>
                      <div className="flex flex-wrap gap-1 ml-3">
                        {student.tags?.map((tag, i) => (
                          <span
                            key={i}
                            className={`text-xs font-semibold px-2 py-1 rounded ${
                              tag === 'DFG' ? 'bg-green-100 text-green-800' :
                              tag.startsWith('DFG_') ? 'bg-teal-100 text-teal-800' :
                              tag.startsWith('BPG_') ? 'bg-orange-100 text-orange-800' :
                              'bg-yellow-100 text-yellow-800'
                            }`}
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Export Button */}
          {filteredOrders.length > 0 && (
            <button
              onClick={() => {
                // CSV export logic
                const headers = ['Student Name', 'Student Email', 'Counties', 'Order Type', 'Order Date', 'Delivery Date', 'Status'];
                const csvRows = [headers.join(',')];

                filteredOrders.forEach(order => {
                  const counties = order.counties.map(c => `${c.county} ${c.state}`).join('; ');
                  const row = [
                    `"${order.studentName}"`,
                    order.studentEmail,
                    `"${counties}"`,
                    order.orderType,
                    order.submittedDate,
                    order.deliveryDate || '',
                    order.status
                  ];
                  csvRows.push(row.join(','));
                });

                const csvContent = csvRows.join('\n');
                const blob = new Blob([csvContent], { type: 'text/csv' });
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `dfg-orders-${new Date().toISOString().split('T')[0]}.csv`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                window.URL.revokeObjectURL(url);
              }}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-semibold flex items-center space-x-2 whitespace-nowrap"
            >
              <Download className="w-4 h-4" />
              <span>Export ({filteredOrders.length})</span>
            </button>
          )}
        </div>
      </div>

      {/* Orders Table */}
      <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b-2 border-gray-200">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Student</th>
                <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Counties</th>
                <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Group Tags</th>
                <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Order Type</th>
                <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Order Date</th>
                <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Delivery Date</th>
                <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Status</th>
                <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredOrders.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-6 py-12 text-center">
                    <div className="flex flex-col items-center space-y-2">
                      <FileSpreadsheet className="w-12 h-12 text-gray-300" />
                      <p className="text-gray-500">No DFG orders found</p>
                      <p className="text-xs text-gray-400">
                        {statusFilter !== 'all' || studentSearchTerm ? 'Try adjusting your filters' : 'Click "Create Order" to get started'}
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                filteredOrders.map((order) => (
                  <tr
                    key={order.orderId}
                    onClick={() => setEditingOrder(order)}
                    className="hover:bg-gray-50 transition-colors cursor-pointer"
                  >
                    <td className="px-6 py-4">
                      <div className="font-semibold text-gray-900">{order.studentName}</div>
                      <div className="text-sm text-gray-500">{order.studentEmail}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-700">
                        {order.counties.map(c => `${c.county}, ${c.state}`).join(' • ')}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-wrap gap-1">
                        {order.groupTags && order.groupTags.length > 0 ? (
                          order.groupTags.map(tag => (
                            <span key={tag} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
                              {tag}
                            </span>
                          ))
                        ) : (
                          <span className="text-sm text-gray-400">-</span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-700">
                        {order.orderType === 'new_list' ? 'New List' : 'Additional Records'}
                        {order.orderType === 'additional_records' && order.additionalRecordsCount && (
                          <div className="text-xs text-gray-500">
                            ({order.additionalRecordsCount} records)
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-700">{formatDate(order.submittedDate)}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-700">
                        {order.deliveryDate ? formatDate(order.deliveryDate) : '-'}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {getStatusBadge(order.status)}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-2">
                        {order.downloadUrl && (
                          <a
                            href={order.downloadUrl}
                            download
                            onClick={(e) => e.stopPropagation()}
                            className="p-1.5 text-green-600 hover:bg-green-50 rounded transition-colors"
                            title="Download"
                          >
                            <Download className="w-4 h-4" />
                          </a>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create Order Modal */}
      <DFPBOrderForm
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={() => {
          showSuccess('DFPB order created successfully!');
          fetchOrders(); // Refresh orders list
        }}
      />

      {/* Edit Order Modal */}
      {editingOrder && (
        <DFPBOrderEditModal
          order={{
            orderId: editingOrder.orderId,
            studentName: editingOrder.studentName,
            studentEmail: editingOrder.studentEmail,
            counties: editingOrder.counties.map(c => `${c.county}, ${c.state}`),
            orderType: editingOrder.orderType,
            quantity: editingOrder.additionalRecordsCount || 0,
            status: editingOrder.status,
            adminNotes: editingOrder.notes || '',
            downloadLink: editingOrder.downloadUrl || null,
            submittedDate: editingOrder.submittedDate,
            deliveredDate: editingOrder.deliveryDate || null
          }}
          onClose={() => setEditingOrder(null)}
          onSaved={() => {
            fetchOrders();
            setEditingOrder(null);
          }}
        />
      )}
    </div>
  );
}
